import { db } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { PLAN_LIMITS } from "./storageUtils";

export const PLAN_FEATURES = {
  standard: {
    name: "Standard",
    storage: "1 GB",
    accounts: 1,
    features: [
      "Basic trade tracking",
      "Journal entries",
      "Basic analytics",
      "Single account",
    ],
  },
  pro: {
    name: "Pro",
    storage: "5 GB",
    accounts: 20,
    features: [
      "Advanced trade tracking",
      "Multiple accounts",
      "Advanced analytics",
      "Priority support",
      "Extended history",
    ],
  },
};

// Get user's current plan
export const getUserPlan = async (userId) => {
  try {
    const planRef = doc(db, "userPlans", userId);
    const planDoc = await getDoc(planRef);

    if (!planDoc.exists()) {
      // Initialize standard plan if none exists
      await setDoc(planRef, {
        userId,
        planType: "standard",
        startDate: serverTimestamp(),
        endDate: null,
        maxStorage: PLAN_LIMITS.standard.maxStorage,
        maxAccounts: PLAN_LIMITS.standard.maxAccounts,
        isActive: true,
        autoRenew: false,
        lastUpdated: serverTimestamp(),
      });
      return {
        planType: "standard",
        ...PLAN_FEATURES.standard,
      };
    }

    const planData = planDoc.data();
    return {
      ...planData,
      ...PLAN_FEATURES[planData.planType],
    };
  } catch (error) {
    console.error("Error getting user plan:", error);
    throw error;
  }
};

// Upgrade user to pro plan
export const upgradeToPro = async (userId) => {
  try {
    const planRef = doc(db, "userPlans", userId);
    await updateDoc(planRef, {
      planType: "pro",
      maxStorage: PLAN_LIMITS.pro.maxStorage,
      maxAccounts: PLAN_LIMITS.pro.maxAccounts,
      lastUpdated: serverTimestamp(),
    });

    return {
      success: true,
      message: "Successfully upgraded to Pro plan",
    };
  } catch (error) {
    console.error("Error upgrading plan:", error);
    throw error;
  }
};

// Downgrade to standard plan
export const downgradeToStandard = async (userId) => {
  try {
    // Check if user meets standard plan limits
    const { currentStorage } = await checkStorageLimit(userId);
    const { currentAccounts } = await canCreateNewAccount(userId);

    if (currentStorage > PLAN_LIMITS.standard.maxStorage) {
      throw new Error("Cannot downgrade: Storage exceeds standard plan limit");
    }

    if (currentAccounts > PLAN_LIMITS.standard.maxAccounts) {
      throw new Error("Cannot downgrade: Too many accounts for standard plan");
    }

    const planRef = doc(db, "userPlans", userId);
    await updateDoc(planRef, {
      planType: "standard",
      maxStorage: PLAN_LIMITS.standard.maxStorage,
      maxAccounts: PLAN_LIMITS.standard.maxAccounts,
      lastUpdated: serverTimestamp(),
    });

    return {
      success: true,
      message: "Successfully downgraded to Standard plan",
    };
  } catch (error) {
    console.error("Error downgrading plan:", error);
    throw error;
  }
};

// Check if feature is available in user's plan
export const isFeatureAvailable = async (userId, feature) => {
  try {
    const plan = await getUserPlan(userId);
    return plan.features.includes(feature);
  } catch (error) {
    console.error("Error checking feature availability:", error);
    throw error;
  }
};
